/**
 * Grammatik as the shared grammar components see it.
 *
 * The adapter and nothing else: field names in, neutral shapes out. Everything
 * this file maps — `prompt_de`, `case_sensitive`, `examples[].de` — is right
 * where it is stored, and wrong inside a component that has to serve two
 * courses. See `src/apps/shared/grammar/types.ts` for the full argument.
 */
import type { GrammarCourse, GrammarExercise, GrammarLessonView } from '../shared/grammar/types';

import { isGrammarAnswerCorrect, isOrderCorrect } from './answer';
import { BLOCK_NAMES, type Anchor, type GrammatikLesson, type GrammatikPayload } from './types';

const ANCHOR_LABEL: Record<Anchor, string> = {
  pt: 'like Portuguese',
  en: 'like English',
  both: 'like both',
  neither: 'like neither',
};

const ANCHOR_STYLE: Record<Anchor, string> = {
  pt: 'border-success/30 bg-success/10 text-success',
  en: 'border-primary/30 bg-primary/10 text-primary',
  both: 'border-border bg-muted text-muted-foreground',
  neither: 'border-destructive/40 bg-destructive/10 text-destructive font-semibold',
};

export const grammatikCourse: GrammarCourse = {
  appId: 'grammatik',
  // The learner is Brazilian, so Portuguese and English are what German gets
  // explained in. Her own primary wins at render time; this is the fallback
  // order.
  aux: ['pt', 'en'],
  anchorLabel: (anchor) => ANCHOR_LABEL[anchor as Anchor] ?? anchor,
  anchorStyle: (anchor) => ANCHOR_STYLE[anchor as Anchor] ?? ANCHOR_STYLE.both,
  blockName: (block) => BLOCK_NAMES[block] ?? `Block ${block}`,
  isAnswerCorrect: isGrammarAnswerCorrect,
  isOrderCorrect,
};

export function toExercise(payload: GrammatikPayload): GrammarExercise {
  const base = { id: payload.id, strict: payload.case_sensitive };
  switch (payload.kind) {
    case 'fill_blank':
      return {
        ...base,
        kind: 'fill_blank',
        prompt: payload.prompt_de,
        answer: payload.answer,
        accept: payload.accept,
        hint: payload.hint,
      };
    case 'choice':
      return {
        ...base,
        kind: 'choice',
        prompt: payload.prompt_de,
        instruction: payload.instruction,
        options: payload.options,
        answer: payload.answer,
      };
    case 'transform':
      return {
        ...base,
        kind: 'transform',
        given: payload.given_de,
        instruction: payload.instruction,
        answer: payload.answer,
        accept: payload.accept,
      };
    case 'order':
      return {
        ...base,
        kind: 'order',
        chunks: payload.chunks,
        answer: payload.answer,
        also_accept: payload.also_accept,
      };
  }
}

export function toLessonView(lesson: GrammatikLesson): GrammarLessonView {
  return {
    n: lesson.n,
    slug: lesson.slug,
    block: lesson.block,
    title: lesson.title.de,
    rule: lesson.rule,
    anchor: lesson.anchor,
    parallel: lesson.parallel
      ? {
          target: lesson.parallel.de,
          aux: {
            ...(lesson.parallel.pt ? { pt: lesson.parallel.pt } : {}),
            ...(lesson.parallel.en ? { en: lesson.parallel.en } : {}),
          },
        }
      : null,
    // The German course has no relief field. The shared screen renders nothing
    // when it is null, so adding them later needs no change here beyond the
    // authoring schema.
    relief: null,
    trap: lesson.trap,
    table: lesson.table,
    examples: lesson.examples.map((example) => ({
      target: example.de,
      focus: example.focus,
      aux: { pt: example.pt, en: example.en },
    })),
  };
}
